import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Stack } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { observer } from 'mobx-react'
import React, { FC } from 'react'
import { useForm, UseFormWatch } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'
import z from 'zod'
import { useInvalidateQueries } from '../../../../../hooks/useInvalidateQueries'
import { isDealerWE } from '../../../../../services/AccountService'
import { updateRequest } from '../../../../../services/ApiService'
import { notifySuccess } from '../../../../../services/ToastifyService/ToastifyService'
import { getComp } from '../../../../../utils/utils/DI'
import FormInput from '../../../../SingleRequestPage/components/SingleRequestDrawer/elements/FormInput'
import FormSelect from '../../../../SingleRequestPage/components/SingleRequestDrawer/elements/FormSelect'
import { getCollections } from '../../../../SingleRequestPage/components/SingleRequestDrawer/helpers/getCollections'
import { RequestType } from '../../../../SingleRequestPage/types/Request.type'
import { REPORT_KEYS } from '../../../../SingleRequestReport/consts/request-report.keys'
import { newRequestDrawerStore } from '../../../stores/NewRequestDrawerStore'

type EditPartialCustomerInfoProps = {
  request: RequestType
  onClose: () => void
  isAllowedAndFullDotation: boolean
}

export const EditPartialCustomerInfo: FC<EditPartialCustomerInfoProps> = ({
  request,
  onClose,
  isAllowedAndFullDotation,
}) => {
  const invalidate = useInvalidateQueries()
  const { getInstallationReasonName } = getComp('InstallationReasonStore')
  const reasonName = getInstallationReasonName(request.reasonId)

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ChangeInfoFormType>({
    defaultValues: getDefaultInfoValues(request, reasonName),
    mode: 'onChange',
    resolver: zodResolver(changeInfoSchema()),
  })

  const { mutateAsync, isPending } = useMutation({
    mutationKey: ['update-request-by-dealer'],
    mutationFn: (params: ChangeInfoFormType) =>
      updateRequest(request.id, params),
    onSuccess: (data) => {
      newRequestDrawerStore.updateRequest(data)
      invalidate(['requests-table', REPORT_KEYS.GET_REPORT_TABLE])
      notifySuccess('Заявка обновлена')
      onClose()
    },
  })

  const { reasonsItems } = getCollections(
    request.territory,
    request.district,
    request.locality,
    request.reasonId,
  )

  const onSave = async (data: ChangeInfoFormType) => {
    let params: Record<string, any> = {}
    if (!isDealerWE())
      params = {
        name: data.name,
        surname: data.surname,
        patronymic: data.patronymic,
        phone: data.phone,
      }

    if (isAllowedAndFullDotation) {
      params.reasonId = data.reasonId?.key ?? request.reasonId
    }

    await mutateAsync(params as ChangeInfoFormType)
  }

  const disableSaveBtn =
    !!Object.keys(errors).length ||
    !isAtLeastOneChanged(getDefaultInfoValues(request, reasonName), watch)

  return (
    <Stack
      height={'100%'}
      justifyContent={'space-between'}
      component={'form'}
      onSubmit={handleSubmit(onSave)}
    >
      <Stack spacing={'16px'}>
        {isAllowedAndFullDotation && (
          <FormSelect
            control={control}
            fullWidth
            name='reasonId'
            label='Причина обращения'
            items={reasonsItems}
            placeholder=''
            withLabel
            width={'350px'}
          />
        )}
        {!isDealerWE() && (
          <>
            <FormInput
              control={control}
              fullWidth
              name='surname'
              label='Фамилия'
              capitalizeFirstLetter
              placeholder=''
            />
            <FormInput
              control={control}
              fullWidth
              name='name'
              label='Имя'
              capitalizeFirstLetter
              placeholder=''
            />
            <FormInput
              control={control}
              fullWidth
              label='Отчество'
              name='patronymic'
              placeholder=''
              capitalizeFirstLetter
            />
            <FormInput
              control={control}
              fullWidth
              name='phone'
              label='Телефон'
              placeholder=''
              mask={'+7 (999)-999-99-99'}
            />
          </>
        )}
      </Stack>
      <Stack direction={'row'} gap={'8px'} mt={'24px'}>
        <Button
          color={'primary'}
          variant={'contained'}
          fullWidth
          disabled={disableSaveBtn}
          type='submit'
          loading={isPending}
        >
          <FormattedMessage id={'применить изменения'} />
        </Button>
        <Button
          color={'primary'}
          variant={'outlined'}
          onClick={onClose}
          fullWidth
        >
          <FormattedMessage id={'Отмена'} />
        </Button>
      </Stack>
    </Stack>
  )
}

export const baseOptionSchema = z.object({
  key: z.number(),
  value: z.string(),
  iconSrc: z.string().optional(),
})

export const changeInfoSchema = () =>
  z.object({
    reasonId: baseOptionSchema.nullable(),
    name: z.string().nullable(),
    surname: z.string().nullable(),
    patronymic: z.string().nullable(),
    phone: z.string().nullable(),
  })

export const getDefaultInfoValues = (
  request: RequestType,
  reasonName: any,
): ChangeInfoFormType => {
  return {
    reasonId: {
      key: request.reasonId ?? 1,
      value: reasonName ?? 'Не определена',
    },
    name: request.name,
    patronymic: request.patronymic,
    phone: request.phone,
    surname: request.surname,
  }
}

export type ChangeInfoFormType = z.infer<ReturnType<typeof changeInfoSchema>>

const isAtLeastOneChanged = (
  defaultValues: ChangeInfoFormType,
  watch: UseFormWatch<ChangeInfoFormType>,
) => {
  let result = false
  const keys = Object.keys(defaultValues) as Array<keyof ChangeInfoFormType>

  keys.forEach((key) => {
    const currentItem = defaultValues[key]

    if (Array.isArray(currentItem)) {
      currentItem.forEach((item, index) => {
        // @ts-ignore
        if (item !== watch(key)?.[index]) {
          result = true
        }
      })
    } else if (typeof currentItem === 'object' && !!currentItem) {
      //  @ts-ignore
      if (currentItem?.key !== watch(key)?.key) {
        result = true
      }
    } else {
      if (currentItem !== watch(key)) {
        result = true
      }
    }
  })

  return result
}

export default observer(EditPartialCustomerInfo)
